import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Calendar, CheckCircle2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";

const WEEKDAYS_NL = ["ma", "di", "wo", "do", "vr", "za", "zo"];
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CopyDaysModal({ open, onClose, weekStart, shiftsByDate, onDone, onConflicts }) {
  const { t } = useI18n();
  const [sources, setSources] = useState(new Set());
  const [targets, setTargets] = useState(new Set());
  const [keepEmployee, setKeepEmployee] = useState(true);
  const [busy, setBusy] = useState(false);

  // Current week days for source selection
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  // Target range: next 4 weeks
  const targetDays = useMemo(() => {
    const start = addDays(weekStart, 7);
    return Array.from({ length: 28 }, (_, i) => addDays(start, i));
  }, [weekStart]);

  const toggle = (setFn, set, key) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setFn(next);
  };

  const sourceCount = sources.size;
  const targetCount = targets.size;
  const sourceShifts = useMemo(
    () => Array.from(sources).reduce((n, d) => n + (shiftsByDate[d]?.length || 0), 0),
    [sources, shiftsByDate]
  );

  const submit = async () => {
    if (sourceCount === 0 || targetCount === 0) {
      toast.error(t("copy_pick_both"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shifts/copy-days", {
        source_dates: Array.from(sources),
        target_dates: Array.from(targets),
        keep_employee: keepEmployee,
      });
      const skipped = data.skipped || [];
      if (skipped.length > 0) {
        // Let parent show conflict summary modal
        onConflicts?.(data.created, skipped);
      } else {
        toast.success(`${data.created} ${t("copied_shifts")}`);
      }
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={onClose}
          data-testid="copy-days-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="card w-full max-w-3xl p-0 overflow-hidden max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            <div className="flex items-start justify-between p-6 border-b hairline">
              <div>
                <div className="eyebrow flex items-center gap-1.5"><Copy size={11} /> {t("copy_days_title")}</div>
                <h3 className="font-display text-[22px] font-bold tracking-tight mt-1">{t("copy_days_h")}</h3>
                <p className="text-[12px] text-[hsl(var(--text-muted))] mt-1 max-w-md">{t("copy_days_hint")}</p>
              </div>
              <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))]" data-testid="copy-days-close">
                <X size={16} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {/* Step 1 — Source */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-bold grid place-items-center">1</span>
                  <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("copy_step_source")}</div>
                  <span className="text-[11px] text-[hsl(var(--text-muted))] ml-1">
                    {sourceCount > 0 && `· ${sourceCount} ${t("day_plural")} · ${sourceShifts} ${t("shifts_selected")}`}
                  </span>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {days.map((d) => {
                    const key = fmtDate(d);
                    const active = sources.has(key);
                    const count = (shiftsByDate[key] || []).length;
                    return (
                      <button
                        key={key}
                        onClick={() => toggle(setSources, sources, key)}
                        data-testid={`copy-source-${key}`}
                        className={`p-2.5 rounded-2xl border transition-all text-center ${
                          active
                            ? "bg-[hsl(var(--primary))] border-transparent text-white shadow-[var(--shadow-sm)]"
                            : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-soft))]"
                        }`}
                      >
                        <div className="text-[9px] uppercase font-bold tracking-wider opacity-80">{WEEKDAYS_NL[(d.getDay()+6)%7]}</div>
                        <div className="font-display text-[18px] font-bold tabular-nums">{d.getDate()}</div>
                        <div className="text-[10px] mt-0.5 opacity-90 tabular-nums">{count} {t("shifts_short")}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2 — Target */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-bold grid place-items-center">2</span>
                  <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("copy_step_target")}</div>
                  <span className="text-[11px] text-[hsl(var(--text-muted))] ml-1">
                    {targetCount > 0 && `· ${targetCount} ${t("day_plural")}`}
                  </span>
                </div>
                {[0, 1, 2, 3].map((wIdx) => {
                  const weekDays = targetDays.slice(wIdx * 7, wIdx * 7 + 7);
                  const wStart = weekDays[0];
                  return (
                    <div key={wIdx} className="mb-2">
                      <div className="text-[10px] uppercase font-semibold tracking-wider text-[hsl(var(--text-muted))] mb-1 flex items-center justify-between">
                        <span>{t("week")} {wStart.toLocaleDateString("nl", { day: "numeric", month: "short" })} – {addDays(wStart, 6).toLocaleDateString("nl", { day: "numeric", month: "short" })}</span>
                        <button
                          className="normal-case font-medium text-[hsl(var(--primary))] hover:underline text-[10px]"
                          data-testid={`copy-week-toggle-${wIdx}`}
                          onClick={() => {
                            const next = new Set(targets);
                            const keys = weekDays.map(fmtDate);
                            const all = keys.every((k) => next.has(k));
                            keys.forEach((k) => all ? next.delete(k) : next.add(k));
                            setTargets(next);
                          }}
                        >
                          {t("select_week")}
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {weekDays.map((d) => {
                          const key = fmtDate(d);
                          const active = targets.has(key);
                          return (
                            <button
                              key={key}
                              onClick={() => toggle(setTargets, targets, key)}
                              data-testid={`copy-target-${key}`}
                              className={`p-1.5 rounded-xl border text-center text-[11px] transition-all ${
                                active
                                  ? "bg-[hsl(var(--success))] border-transparent text-white"
                                  : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-soft))]"
                              }`}
                            >
                              <div className="text-[8px] uppercase font-semibold opacity-80">{WEEKDAYS_NL[(d.getDay()+6)%7]}</div>
                              <div className="font-display text-[14px] font-bold tabular-nums">{d.getDate()}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Options */}
              <div className="rounded-2xl bg-[hsl(var(--muted))] p-3 flex items-center gap-2.5">
                <input
                  type="checkbox" id="keepEmp" checked={keepEmployee}
                  onChange={(e) => setKeepEmployee(e.target.checked)}
                  data-testid="copy-keep-employee"
                />
                <label htmlFor="keepEmp" className="flex items-center gap-1.5 text-[12px] cursor-pointer select-none">
                  <UserIcon size={12} strokeWidth={2} className="text-[hsl(var(--primary))]" />
                  <span className="text-[hsl(var(--text-strong))] font-semibold">{t("copy_keep_employee")}</span>
                  <span className="text-[hsl(var(--text-muted))]">— {t("copy_keep_employee_hint")}</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t hairline bg-[hsl(var(--bg))]">
              <div className="text-[12px] text-[hsl(var(--text-muted))]">
                {sourceCount > 0 && targetCount > 0 && (
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 size={12} className="text-[hsl(var(--success))]" />
                    {sourceShifts * targetCount} {t("shifts_will_be_created")}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={onClose} className="btn btn-quiet">{t("cancel")}</button>
                <button
                  onClick={submit} disabled={busy || sourceCount === 0 || targetCount === 0}
                  className="btn btn-primary" data-testid="copy-days-submit"
                >
                  <Copy size={14} /> {busy ? t("copying") : t("copy_now")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
